import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTimeTable } from './contexts/TimeTableContext';
import { useAuth } from './common/AuthContext';
import { authFetch } from './common/authFetch';
import { formatLocalDate, parseLocalDate } from './common/utils';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { downloadImage, canvasToBlob } from './common/shareService';
import ShareDialog from './components/ShareDialog';
import './css/Daily.css';
import './css/Statistics.css';
import './css/Dialogs.css';

const Daily = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const queryParams = new URLSearchParams(location.search);
    const targetUserId = queryParams.get('userId');
    const isReadOnly = !!targetUserId;

    // Observer: student selector
    const [myStudents, setMyStudents] = useState([]);
    useEffect(() => {
        const fetchMyStudents = async () => {
            if (!user) return;
            try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/observer/students`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
                });
                const data = await res.json();
                if (data.success) setMyStudents(data.students);
            } catch (e) { console.error("Failed to fetch students", e); }
        };
        fetchMyStudents();
    }, [user]);

    const handleStudentChange = (e) => {
        const newUserId = e.target.value;
        navigate(newUserId ? `/daily?userId=${newUserId}` : '/daily');
    };
    const [currentDate, setCurrentDate] = useState(new Date());
    const [viewMode, setViewMode] = useState('week'); // 'day', 'week', 'month'
    const {
        fetchOneDaySchedule,
        fetchScheduleRange,
        oneDaySchedules,
        subjects,
        fetchSubjects,
        categories,
        fetchCategories,
        createTodoSchedule,
        toggleTodoCompleted,
        startWithMonday
    } = useTimeTable();

    // Print / Export states
    const printRef = useRef(null);
    const [isPdfExporting, setIsPdfExporting] = useState(false);
    const [pdfPreview, setPdfPreview] = useState(null);
    const [isPngExporting, setIsPngExporting] = useState(false);
    const [pngPreview, setPngPreview] = useState(null);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareCardData, setShareCardData] = useState({});

    // Todo add dialog state
    const [todoDialogOpen, setTodoDialogOpen] = useState(false);
    const [todoSubjectId, setTodoSubjectId] = useState('');
    const [todoDescription, setTodoDescription] = useState('');

    // Category filter state
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [hiddenCategories, setHiddenCategories] = useState(new Set());
    const [filterLoaded, setFilterLoaded] = useState(false);

    // Load saved filter from server (observer mode: load student's filter)
    useEffect(() => {
        if (!user) { setFilterLoaded(true); return; }
        setHiddenCategories(new Set());
        setFilterLoaded(false);
        const loadFilter = async () => {
            try {
                const filterUrl = targetUserId
                    ? `${process.env.REACT_APP_BACKEND_URL}/api/v1/user/checklist-filter?userId=${targetUserId}`
                    : `${process.env.REACT_APP_BACKEND_URL}/api/v1/user/checklist-filter`;
                const res = await authFetch(filterUrl);
                const data = await res.json();
                if (data.success && Array.isArray(data.hiddenCategories)) {
                    setHiddenCategories(new Set(data.hiddenCategories));
                }
            } catch (e) { console.error('Failed to load checklist filter:', e); }
            finally { setFilterLoaded(true); }
        };
        loadFilter();
    }, [user, targetUserId]);

    // Save filter to server
    const saveFilterToServer = useCallback(async (hidden) => {
        if (!user) return;
        try {
            await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/user/checklist-filter`, {
                method: 'PUT',
                body: JSON.stringify({ hiddenCategories: [...hidden] })
            });
        } catch (e) { console.error('Failed to save checklist filter:', e); }
    }, [user]);

    // iOS Safari: 모달 열릴 때 배경 스크롤 방지
    useEffect(() => {
        if (todoDialogOpen || filterDialogOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [todoDialogOpen, filterDialogOpen]);

    useEffect(() => {
        fetchSubjects(targetUserId);
        fetchCategories(targetUserId);
    }, [targetUserId]);

    const getDateRange = useCallback((date, mode) => {
        const start = new Date(date);
        const end = new Date(date);
        if (mode === 'day') {
            start.setHours(0, 0, 0, 0);
            end.setDate(end.getDate() + 1);
            end.setHours(0, 0, 0, 0);
        } else if (mode === 'week') {
            const dayOfWeek = start.getDay();
            if (startWithMonday) {
                const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                start.setDate(start.getDate() - diff);
            } else {
                start.setDate(start.getDate() - dayOfWeek);
            }
            start.setHours(0, 0, 0, 0);
            end.setTime(start.getTime());
            end.setDate(end.getDate() + 7);
            end.setHours(0, 0, 0, 0);
        } else if (mode === 'month') {
            start.setDate(1);
            start.setHours(0, 0, 0, 0);
            end.setMonth(end.getMonth() + 1);
            end.setDate(1);
            end.setHours(0, 0, 0, 0);
        }
        return { start, end };
    }, [startWithMonday]);

    useEffect(() => {
        if (viewMode === 'day') {
            fetchOneDaySchedule(currentDate, targetUserId);
        } else {
            const { start, end } = getDateRange(currentDate, viewMode);
            fetchScheduleRange(start, end, targetUserId);
        }
    }, [currentDate, viewMode, targetUserId]);

    // Filter schedules by hidden categories
    const filteredSchedules = useMemo(() => {
        if (hiddenCategories.size === 0) return oneDaySchedules || [];
        return (oneDaySchedules || []).filter(s => {
            const catId = s.study_subject?.category_id;
            return !hiddenCategories.has(catId);
        });
    }, [oneDaySchedules, hiddenCategories]);

    // Split schedules into todos and timed
    const { todoSchedules, timedSchedules } = useMemo(() => {
        const todos = [];
        const timed = [];
        filteredSchedules.forEach(s => {
            const isTodo = s.schedule_type === 'todo' || (s.scheduled_time === 0 && s.schedule_type !== 'timed');
            if (isTodo) {
                todos.push(s);
            } else {
                timed.push(s);
            }
        });
        // Sort by start_time
        timed.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
        todos.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
        return { todoSchedules: todos, timedSchedules: timed };
    }, [filteredSchedules]);

    // Group by date for week/month views
    const groupedByDate = useMemo(() => {
        if (viewMode === 'day') return null;
        const groups = {};
        filteredSchedules.forEach(s => {
            const d = new Date(s.start_time);
            const key = formatLocalDate(d);
            if (!groups[key]) groups[key] = { date: d, todos: [], timed: [] };
            const isTodo = s.schedule_type === 'todo' || (s.scheduled_time === 0 && s.schedule_type !== 'timed');
            if (isTodo) groups[key].todos.push(s);
            else groups[key].timed.push(s);
        });
        // Sort timed within each group
        Object.values(groups).forEach(g => {
            g.timed.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
        });
        return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
    }, [filteredSchedules, viewMode]);

    // Weekly grid: 7 days array (always show all 7 days)
    const weeklyGrid = useMemo(() => {
        if (viewMode !== 'week') return null;
        const { start } = getDateRange(currentDate, 'week');
        const groups = {};
        filteredSchedules.forEach(s => {
            const d = new Date(s.start_time);
            const key = formatLocalDate(d);
            if (!groups[key]) groups[key] = { todos: [], timed: [] };
            const isTodo = s.schedule_type === 'todo' || (s.scheduled_time === 0 && s.schedule_type !== 'timed');
            if (isTodo) groups[key].todos.push(s);
            else groups[key].timed.push(s);
        });
        const days = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(start);
            d.setDate(d.getDate() + i);
            const key = formatLocalDate(d);
            const data = groups[key] || { todos: [], timed: [] };
            data.timed.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
            days.push({ date: d, key, ...data });
        }
        return days;
    }, [viewMode, currentDate, filteredSchedules, getDateRange]);

    // Monthly grid: each cell = 1 week, all schedules merged
    const monthlyGrid = useMemo(() => {
        if (viewMode !== 'month') return null;
        const { start } = getDateRange(currentDate, 'month');
        const year = start.getFullYear();
        const month = start.getMonth();
        const lastDay = new Date(year, month + 1, 0).getDate();

        // Group all schedules by date
        const dateGroups = {};
        filteredSchedules.forEach(s => {
            const d = new Date(s.start_time);
            const key = formatLocalDate(d);
            if (!dateGroups[key]) dateGroups[key] = [];
            dateGroups[key].push(s);
        });

        // Build week ranges
        const weeks = [];
        const firstDayOfMonth = new Date(year, month, 1);
        const firstDayOfWeek = firstDayOfMonth.getDay();
        let weekStart;
        if (startWithMonday) {
            const diff = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
            weekStart = new Date(year, month, 1 - diff);
        } else {
            weekStart = new Date(year, month, 1 - firstDayOfWeek);
        }

        while (true) {
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekEnd.getDate() + 6);
            const allItems = [];
            for (let i = 0; i < 7; i++) {
                const d = new Date(weekStart);
                d.setDate(d.getDate() + i);
                const key = formatLocalDate(d);
                if (dateGroups[key]) allItems.push(...dateGroups[key]);
            }
            allItems.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
            const fmt = (d) => `${d.getMonth() + 1}.${d.getDate()}`;
            weeks.push({
                label: `${fmt(weekStart)} ~ ${fmt(weekEnd)}`,
                startDate: new Date(weekStart),
                items: allItems
            });
            weekStart.setDate(weekStart.getDate() + 7);
            if (weekStart.getMonth() > month || (weekStart.getMonth() === 0 && month === 11)) break;
            if (weekStart > new Date(year, month, lastDay)) break;
        }
        return weeks;
    }, [viewMode, currentDate, filteredSchedules, getDateRange, startWithMonday]);

    const handlePrev = () => {
        const d = new Date(currentDate);
        if (viewMode === 'day') d.setDate(d.getDate() - 1);
        else if (viewMode === 'week') d.setDate(d.getDate() - 7);
        else if (viewMode === 'month') d.setMonth(d.getMonth() - 1);
        setCurrentDate(d);
    };

    const handleNext = () => {
        const d = new Date(currentDate);
        if (viewMode === 'day') d.setDate(d.getDate() + 1);
        else if (viewMode === 'week') d.setDate(d.getDate() + 7);
        else if (viewMode === 'month') d.setMonth(d.getMonth() + 1);
        setCurrentDate(d);
    };

    const getPeriodLabel = () => {
        if (viewMode === 'day') {
            return currentDate.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
        } else if (viewMode === 'week') {
            const { start, end } = getDateRange(currentDate, 'week');
            const endDisplay = new Date(end.getTime() - 1); // last day of the week
            const fmt = (d) => `${d.getMonth() + 1}.${d.getDate()}`;
            return `${fmt(start)} ~ ${fmt(endDisplay)}`;
        } else {
            return currentDate.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' });
        }
    };

    const getDateLabel = (date) => {
        const d = new Date(date);
        const weekday = d.toLocaleDateString('ko-KR', { weekday: 'short' });
        return `${d.getMonth() + 1}.${d.getDate()} (${weekday})`;
    };

    const toggleCategoryFilter = (categoryId) => {
        setHiddenCategories(prev => {
            const next = new Set(prev);
            if (next.has(categoryId)) next.delete(categoryId);
            else next.add(categoryId);
            saveFilterToServer(next);
            return next;
        });
    };

    const handleTodoAdd = async () => {
        if (isReadOnly || !todoSubjectId) return;
        await createTodoSchedule(parseInt(todoSubjectId), currentDate, todoDescription || null);
        setTodoDialogOpen(false);
        setTodoSubjectId('');
        setTodoDescription('');
        if (viewMode === 'day') {
            fetchOneDaySchedule(currentDate, targetUserId);
        } else {
            const { start, end } = getDateRange(currentDate, viewMode);
            fetchScheduleRange(start, end, targetUserId);
        }
    };

    // PDF Export
    const handleExportPdf = async () => {
        if (!printRef.current || isPdfExporting) return;
        setIsPdfExporting(true);
        try {
            const canvas = await html2canvas(printRef.current, {
                scale: 2, useCORS: true,
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--card-bg').trim() || '#ffffff',
                logging: false,
            });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const ratio = Math.min((pageW - margin * 2) / canvas.width, (pageH - margin * 2) / canvas.height);
            pdf.addImage(imgData, 'PNG', (pageW - canvas.width * ratio) / 2, margin, canvas.width * ratio, canvas.height * ratio);
            const fileName = `checklist_${viewMode}_${formatLocalDate(currentDate)}.pdf`;
            setPdfPreview({ imgDataUrl: imgData, pdfInstance: pdf, fileName });
        } catch (e) { console.error('PDF export failed:', e); }
        finally { setIsPdfExporting(false); }
    };
    const handlePdfSave = () => { if (pdfPreview) { pdfPreview.pdfInstance.save(pdfPreview.fileName); setPdfPreview(null); } };

    // PNG Export
    const handleExportPng = async () => {
        if (!printRef.current || isPngExporting) return;
        setIsPngExporting(true);
        try {
            const canvas = await html2canvas(printRef.current, {
                scale: 2, useCORS: true,
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--card-bg').trim() || '#ffffff',
                logging: false,
            });
            const blob = await canvasToBlob(canvas);
            const imgDataUrl = canvas.toDataURL('image/png');
            const fileName = `checklist_${viewMode}_${formatLocalDate(currentDate)}.png`;
            setPngPreview({ imgDataUrl, blob, fileName });
        } catch (e) { console.error('PNG export failed:', e); }
        finally { setIsPngExporting(false); }
    };
    const handlePngSave = () => { if (pngPreview) { downloadImage(pngPreview.blob, pngPreview.fileName); setPngPreview(null); } };

    // Share
    const handleShareChecklist = async () => {
        if (!printRef.current) return;
        try {
            const canvas = await html2canvas(printRef.current, {
                scale: 2, useCORS: true,
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--card-bg').trim() || '#ffffff',
                logging: false,
            });
            const imageDataUrl = canvas.toDataURL('image/png');
            const totalItems = filteredSchedules.length;
            const completedItems = filteredSchedules.filter(s => s.completed).length;
            setShareCardData({
                imageDataUrl,
                label: `${t('nav.daily')} - ${viewMode === 'day' ? t('statistics.timeRange.daily') : viewMode === 'week' ? t('statistics.timeRange.weekly') : t('statistics.timeRange.monthly')}`,
                date: new Date().toLocaleDateString(),
                username: user?.username || '',
                totalItems,
                completedItems,
            });
            setShareDialogOpen(true);
        } catch (e) { console.error('Share capture failed:', e); }
    };

    const renderScheduleItem = (schedule) => {
        const startDate = new Date(schedule.start_time);
        const startHour = String(startDate.getHours()).padStart(2, '0');
        const startMinute = String(startDate.getMinutes()).padStart(2, '0');
        const endDate = new Date(startDate.getTime() + schedule.scheduled_time * 60000);
        const endHour = String(endDate.getHours()).padStart(2, '0');
        const endMinute = String(endDate.getMinutes()).padStart(2, '0');

        return (
            <div key={schedule.schedule_id} className={`schedule-item ${schedule.completed ? 'completed' : ''}`} style={{ borderLeft: `5px solid ${schedule.study_subject?.color || '#ccc'}` }}>
                <input
                    type="checkbox"
                    checked={!!schedule.completed}
                    onChange={() => { if (!isReadOnly) toggleTodoCompleted(schedule.schedule_id, !schedule.completed); }}
                    disabled={isReadOnly}
                    className="schedule-check"
                />
                <div className="schedule-time">
                    {startHour}:{startMinute} - {endHour}:{endMinute} ({schedule.scheduled_time} min)
                </div>
                <div className="schedule-content">
                    <h3>{schedule.special_text || schedule.study_subject?.subjectname}</h3>
                    {schedule.special_text && (
                        <p className="schedule-subject-sub">({schedule.study_subject?.subjectname})</p>
                    )}
                </div>
                {!isReadOnly && !schedule.completed && (
                    <button
                        className="todo-daily-timer-btn"
                        onClick={() => navigate('/timer', {
                            state: {
                                subject_id: schedule.subject_id,
                                schedule_id: schedule.schedule_id,
                                subject_name: schedule.study_subject?.subjectname,
                                special_text: schedule.special_text || null,
                                subject_color: schedule.study_subject?.color,
                                schedule_type: 'timed',
                                scheduled_time: schedule.scheduled_time
                            }
                        })}
                    >
                        ▶ {t('timer.startTimer')}
                    </button>
                )}
            </div>
        );
    };

    // Compact list item for week grid (no timer button, just checkbox + text)
    const renderWeekListItem = (schedule) => {
        const isTodo = schedule.schedule_type === 'todo' || (schedule.scheduled_time === 0 && schedule.schedule_type !== 'timed');
        const startDate = new Date(schedule.start_time);
        const timeStr = !isTodo ? `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}` : null;

        return (
            <div
                key={schedule.schedule_id}
                className={`week-grid-item ${schedule.completed ? 'completed' : ''}`}
                style={{ borderLeftColor: schedule.study_subject?.color || '#ccc' }}
            >
                <input
                    type="checkbox"
                    checked={!!schedule.completed}
                    onChange={() => { if (!isReadOnly) toggleTodoCompleted(schedule.schedule_id, !schedule.completed); }}
                    disabled={isReadOnly}
                    className="week-grid-check"
                />
                <span className="week-grid-name">
                    {schedule.special_text
                        ? `[${schedule.study_subject?.subjectname}] ${schedule.special_text}`
                        : schedule.study_subject?.subjectname}
                    {timeStr && <span className="week-grid-time"> ({timeStr}~)</span>}
                </span>
            </div>
        );
    };

    const renderTodoItem = (todo) => (
        <div key={todo.schedule_id} className={`todo-daily-item ${todo.completed ? 'completed' : ''}`}>
            <div className="todo-daily-color-bar" style={{ backgroundColor: todo.study_subject?.color || '#ccc' }} />
            <input
                type="checkbox"
                checked={!!todo.completed}
                onChange={() => { if (!isReadOnly) toggleTodoCompleted(todo.schedule_id, !todo.completed); }}
                disabled={isReadOnly}
                style={{ cursor: isReadOnly ? 'default' : 'pointer' }}
            />
            <div className="todo-daily-content">
                <span className="todo-daily-subject">
                    {todo.special_text || todo.study_subject?.subjectname}
                </span>
                {todo.special_text && (
                    <span className="todo-daily-subject-sub">({todo.study_subject?.subjectname})</span>
                )}
            </div>
            {!isReadOnly && !todo.completed && (
                <button
                    className="todo-daily-timer-btn"
                    onClick={() => navigate('/timer', {
                        state: {
                            subject_id: todo.subject_id,
                            schedule_id: todo.schedule_id,
                            subject_name: todo.study_subject?.subjectname,
                            special_text: todo.special_text || null,
                            subject_color: todo.study_subject?.color,
                            schedule_type: 'todo'
                        }
                    })}
                >
                    ▶ {t('timer.startTimer')}
                </button>
            )}
        </div>
    );

    // Stats summary for week/month
    const summary = useMemo(() => {
        if (viewMode === 'day') return null;
        const totalTodos = todoSchedules.length;
        const completedTodos = todoSchedules.filter(t => t.completed).length;
        const totalTimed = timedSchedules.length;
        const completedTimed = timedSchedules.filter(t => t.completed).length;
        return { totalTodos, completedTodos, totalTimed, completedTimed };
    }, [viewMode, todoSchedules, timedSchedules]);

    return (
        <div className="daily-container">
            {/* View Mode Toggle + Student Selector + Filter */}
            <div className="checklist-top-bar">
                <div className="view-mode-toggle">
                    <button className={viewMode === 'day' ? 'active' : ''} onClick={() => setViewMode('day')}>
                        {t('statistics.timeRange.daily')}
                    </button>
                    <button className={viewMode === 'week' ? 'active' : ''} onClick={() => setViewMode('week')}>
                        {t('statistics.timeRange.weekly')}
                    </button>
                    <button className={viewMode === 'month' ? 'active' : ''} onClick={() => setViewMode('month')}>
                        {t('statistics.timeRange.monthly')}
                    </button>
                </div>
                {myStudents.length > 0 && (
                    <select
                        value={targetUserId || ''}
                        onChange={handleStudentChange}
                        className="checklist-student-select"
                    >
                        <option value="">{t('My Checklist')}</option>
                        {myStudents.filter(s => s.perm_timetable).map(s => (
                            <option key={s.user_id} value={s.user_id}>{s.username}</option>
                        ))}
                    </select>
                )}
                <div className="checklist-export-buttons">
                    <button className="pdf-export-btn" onClick={handleExportPdf} disabled={isPdfExporting}>
                        {isPdfExporting ? t('pdf.exporting') : t('pdf.export')}
                    </button>
                    <button className="png-export-btn" onClick={handleExportPng} disabled={isPngExporting}>
                        {isPngExporting ? t('png.exporting') : t('png.export')}
                    </button>
                    <button className="share-export-btn" onClick={handleShareChecklist}>
                        {t('share.share')}
                    </button>
                </div>
                <button
                    className={`checklist-filter-btn ${hiddenCategories.size > 0 ? 'active' : ''}`}
                    onClick={() => setFilterDialogOpen(true)}
                    title={t('checklist.filter')}
                >
                    ⚙
                </button>
            </div>

            <div ref={printRef}>
            {/* Date Navigation */}
            <div className="checklist-nav">
                {viewMode === 'day' && (
                    <input
                        type="date"
                        value={formatLocalDate(currentDate)}
                        onChange={(e) => setCurrentDate(parseLocalDate(e.target.value))}
                        className="checklist-date-input"
                    />
                )}
                <button onClick={() => setCurrentDate(new Date())} className="checklist-today-btn">
                    {t('today')}
                </button>
            </div>
            <div className="checklist-period-nav">
                <button onClick={handlePrev} className="checklist-arrow-btn">◀</button>
                <h2 className="checklist-period-label">{getPeriodLabel()}</h2>
                <button onClick={handleNext} className="checklist-arrow-btn">▶</button>
            </div>

            {/* Summary for week/month */}
            {summary && (
                <div className="checklist-summary">
                    <div className="checklist-summary-item">
                        <span className="checklist-summary-label">{t('achievement.schedule')}</span>
                        <span className="checklist-summary-value">{summary.completedTimed}/{summary.totalTimed}</span>
                    </div>
                    <div className="checklist-summary-item">
                        <span className="checklist-summary-label">{t('achievement.todoTitle')}</span>
                        <span className="checklist-summary-value">{summary.completedTodos}/{summary.totalTodos}</span>
                    </div>
                </div>
            )}

            {/* Day View */}
            {viewMode === 'day' && (
                <>
                    <div className="daily-todo-section">
                        <h3 className="daily-todo-title">{t('todo.title')}</h3>
                        {todoSchedules.length === 0 ? (
                            <p className="daily-todo-empty">{t('todo.noItems')}</p>
                        ) : todoSchedules.map(renderTodoItem)}
                        {!isReadOnly && (
                            <button
                                className="todo-daily-add-btn"
                                onClick={() => { setTodoDialogOpen(true); setTodoSubjectId(''); setTodoDescription(''); }}
                            >
                                + {t('todo.add')}
                            </button>
                        )}
                    </div>
                    <div className="daily-schedule-list">
                        {timedSchedules.length === 0 ? (
                            <p className="no-schedules">{t('daily.noSchedules')}</p>
                        ) : timedSchedules.map(renderScheduleItem)}
                    </div>
                </>
            )}

            {/* Week View - 4x2 Grid */}
            {viewMode === 'week' && (
                <div className="week-grid">
                    {weeklyGrid && weeklyGrid.map(day => {
                        const isToday = formatLocalDate(new Date()) === day.key;
                        const allItems = [...day.timed, ...day.todos];
                        return (
                            <div key={day.key} className={`week-grid-cell ${isToday ? 'today' : ''}`}>
                                <div className="week-grid-date-header">{getDateLabel(day.date)}</div>
                                <div className="week-grid-items">
                                    {allItems.length === 0 ? (
                                        <p className="week-grid-empty">-</p>
                                    ) : allItems.map(renderWeekListItem)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Month View - 1 cell per week, 4~5 cells */}
            {viewMode === 'month' && monthlyGrid && (
                <div className="month-week-grid">
                    {monthlyGrid.map((week, wi) => (
                        <div key={wi} className="month-week-cell">
                            <div className="month-week-header">{week.label}</div>
                            <div className="month-week-items">
                                {week.items.length === 0 ? (
                                    <p className="week-grid-empty">-</p>
                                ) : week.items.map(renderWeekListItem)}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            </div>{/* end printRef */}

            {/* PDF Preview Dialog */}
            {pdfPreview && (
                <div className="dialog-overlay" onClick={() => setPdfPreview(null)}>
                    <div className="pdf-preview-dialog" onClick={(e) => e.stopPropagation()}>
                        <h3 className="dialog-title">{t('pdf.preview')}</h3>
                        <div className="pdf-preview-image-container">
                            <img src={pdfPreview.imgDataUrl} alt="PDF Preview" className="pdf-preview-image" />
                        </div>
                        <p className="pdf-preview-filename">{pdfPreview.fileName}</p>
                        <div className="dialog-actions">
                            <button className="btn-secondary" onClick={() => setPdfPreview(null)}>{t('cancel')}</button>
                            <button className="btn-primary" onClick={handlePdfSave}>{t('pdf.save')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* PNG Preview Dialog */}
            {pngPreview && (
                <div className="dialog-overlay" onClick={() => setPngPreview(null)}>
                    <div className="pdf-preview-dialog" onClick={(e) => e.stopPropagation()}>
                        <h3 className="dialog-title">{t('png.preview')}</h3>
                        <div className="pdf-preview-image-container">
                            <img src={pngPreview.imgDataUrl} alt="PNG Preview" className="pdf-preview-image" />
                        </div>
                        <p className="pdf-preview-filename">{pngPreview.fileName}</p>
                        <div className="dialog-actions">
                            <button className="btn-secondary" onClick={() => setPngPreview(null)}>{t('cancel')}</button>
                            <button className="btn-primary" onClick={handlePngSave}>{t('png.save')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Share Dialog */}
            <ShareDialog
                open={shareDialogOpen}
                onClose={() => setShareDialogOpen(false)}
                cardType="timetable"
                data={shareCardData}
            />

            {/* Category Filter Dialog */}
            {filterDialogOpen && (
                <div className="dialog-overlay">
                    <div className="dialog-box">
                        <h3 className="dialog-title">{t('checklist.filter')}</h3>
                        <div className="filter-category-list">
                            {categories.map(cat => (
                                <label key={cat.category_id} className="filter-category-item">
                                    <input
                                        type="checkbox"
                                        checked={!hiddenCategories.has(cat.category_id)}
                                        onChange={() => toggleCategoryFilter(cat.category_id)}
                                    />
                                    <span className="filter-category-color" style={{ backgroundColor: cat.color || '#ccc' }} />
                                    <span>{cat.category_name}</span>
                                </label>
                            ))}
                        </div>
                        <div className="dialog-actions">
                            <button className="btn-secondary" onClick={() => { const empty = new Set(); setHiddenCategories(empty); saveFilterToServer(empty); setFilterDialogOpen(false); }}>
                                {t('checklist.showAll')}
                            </button>
                            <button className="btn-primary" onClick={() => setFilterDialogOpen(false)}>
                                {t('confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ToDo Add Dialog */}
            {todoDialogOpen && (
                <div className="dialog-overlay">
                    <div className="dialog-box">
                        <h3 className="dialog-title">{t('todo.add')}</h3>
                        <div>
                            <label className="dialog-label">{t('todo.selectSubject')}</label>
                            <div className="todo-subject-list">
                                {subjects.map(s => (
                                    <div
                                        key={s.subject_id}
                                        className={`todo-subject-option ${String(todoSubjectId) === String(s.subject_id) ? 'selected' : ''}`}
                                        onClick={() => setTodoSubjectId(s.subject_id)}
                                    >
                                        <span className="todo-subject-color" style={{ backgroundColor: s.color || '#ccc' }} />
                                        <span>{s.subjectname}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="dialog-label">{t('todo.addDescription')}</label>
                            <input
                                type="text"
                                value={todoDescription}
                                onChange={(e) => setTodoDescription(e.target.value)}
                                className="dialog-input"
                                onKeyDown={(e) => e.key === 'Enter' && handleTodoAdd()}
                            />
                        </div>
                        <div className="dialog-actions">
                            <button className="btn-secondary" onClick={() => setTodoDialogOpen(false)}>{t('cancel')}</button>
                            <button className="btn-primary" onClick={handleTodoAdd} disabled={!todoSubjectId}>{t('save')}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Daily;
